import { type FC } from 'react';
import cx from 'classnames';

import { PersonSimpleRunIcon } from '../SceneBannerCard/icons';
import { UnstyledButton } from '../UnstyledButton';
import { useI18n } from '../../i18n';

import { DesktopSideMenu, type DesktopMenuItem } from './DesktopSideMenu';
import overlayStyles from './OverallSceneOverlay.module.css';
import styles from './IntroDesktopLayout.module.css';

export type DesktopSlide = {
  key: string;
  title: string | null;
  description: string | null;
  /** When present, scene slide — renders the "Explore X" button. */
  ctaLabel: string | null;
};

type Props = {
  current: DesktopSlide | null;
  previous: DesktopSlide | null;
  /** Side-menu items (welcome + scene labels in slide order). */
  menuItems: DesktopMenuItem[];
  activeIndex: number;
  busy: boolean;
  /** Keyboard / click navigation hint shown below the curtain. */
  hintArrows: string;
  hintAction: string;
  onSelect: (index: number) => void;
  onCtaClick: () => void;
};

/**
 * Desktop variant of the intro overlay. Two-column layout:
 *
 *   - LEFT (`w-582`): title (128 px Amiri Bold — intentionally bleeds out
 *     past the column on the inline-end), description (28 px), optional
 *     "Explore X" CTA button on scene slides.
 *   - RIGHT (`w-117`): `DesktopSideMenu` with the FLIP-animated indicator
 *     bar that morphs between active items.
 *
 * Slide changes drive the same two-slot crossfade as the mobile layout
 * (shared `slotEnter` / `slotLeave` keyframes from
 * `OverallSceneOverlay.module.css`). Below the row, a small caption row
 * shows the keyboard hint ("←/→ arrows • or click to navigate").
 */
export const IntroDesktopLayout: FC<Props> = ({
  current,
  previous,
  menuItems,
  activeIndex,
  busy,
  hintArrows,
  hintAction,
  onSelect,
  onCtaClick,
}) => {
  return (
    <div className={styles.row}>
      <div className={styles.textColumn}>
        <div className={styles.slideStack}>
          {previous && (
            <div
              key={`prev-${previous.key}`}
              className={cx(overlayStyles.slot, overlayStyles.slotLeave)}
              aria-hidden="true"
            >
              <DesktopSlideContent slide={previous} onCtaClick={onCtaClick} />
            </div>
          )}
          {current && (
            <div
              key={`curr-${current.key}`}
              className={cx(overlayStyles.slot, overlayStyles.slotEnter)}
            >
              <DesktopSlideContent slide={current} onCtaClick={onCtaClick} />
            </div>
          )}
        </div>
      </div>

      <DesktopSideMenu
        items={menuItems}
        activeIndex={activeIndex}
        onSelect={onSelect}
        busy={busy}
      />

      <p className={styles.hint}>
        <span className={styles.hintArrows}>{hintArrows}</span>
        <span className={styles.hintSep} aria-hidden="true">•</span>
        <span className={styles.hintAction}>{hintAction}</span>
      </p>
    </div>
  );
};

IntroDesktopLayout.displayName = 'IntroDesktopLayout';

/** Inner slide content for the desktop layout. Static markup — animations
 *  live on the parent slot wrapper. */
const DesktopSlideContent: FC<{
  slide: DesktopSlide;
  onCtaClick: () => void;
}> = ({ slide, onCtaClick }) => {
  const i18n = useI18n();
  return (
    <div className={styles.slide}>
      <div className={styles.text}>
        {slide.title && (
          <h2 className={styles.title} dir="auto">
            {slide.title}
          </h2>
        )}
        {slide.description && (
          <p className={styles.description} dir="auto">
            {slide.description}
          </p>
        )}
      </div>
      {slide.ctaLabel && (
        <UnstyledButton
          className={styles.cta}
          onClick={onCtaClick}
          onPointerDown={(e) => e.stopPropagation()}
          dir="auto"
        >
          {/* Desktop CTA uses the short "Explore" label per design — no
              location name. `slide.ctaLabel` (built upstream from the
              `exploreLocation` template) is consulted only as a presence
              marker for scene slides (welcome has it null). */}
          <span className={styles.ctaLabel}>{i18n.assistant.exploreShort}</span>
          <span className={styles.ctaIcon} aria-hidden="true">
            <PersonSimpleRunIcon />
          </span>
        </UnstyledButton>
      )}
    </div>
  );
};

DesktopSlideContent.displayName = 'DesktopSlideContent';
